import Debug from 'debug'
import { storeToRefs } from 'pinia'
import { RestaurantAreaType, RestaurantAreas } from '~/lib/interfaces/restaurant'
import { Restaurant } from '~/lib/services/store/restaurant/restaurant.dto'
import { getSelectedRestaurantFromCookie } from '~/utils/restaurantUtils'
import {
  Promotion,
  PromotionDeepLinkDataType,
  PromotionDeeplinkType,
  PromotionDeeplinkTypes,
  PromotionKind,
  PromotionType,
  promotionTypeType,
} from '~/lib/services/promos/promos.dto'
import { promosService } from '~/lib/services/promos/promos.service'
import { ToastType } from '~/lib/models/ui/toast'

const debug = Debug('app:composable:useProductDetailRedirect')

export const useProductDetailRedirect = () => {
  const restaurantStore = useRestaurantStore()
  const appStore = useAppStore()
  const { restaurantServiceRef } = useStoreService()
  const { initialFetchLoaded } = storeToRefs(appStore)
  const route = useRoute()
  const { navigateToRestaurantHome, ssoRoute } = useMcdRoutes()
  const restaurantService = restaurantServiceRef()
  const country = useCountry()
  const authStore = useAuthStore()
  const { getCheckPromosInput, getPromotionsBody, applyPromotion, verifyCheckCodeResponse } =
    usePromotions()
  const { isUserLogged } = storeToRefs(authStore)

  const isError = computed(() => restaurantStore.productIdentifierNotFound)
  const willOpenArea = ref<RestaurantAreaType>()
  const isValidArea = (area: string): area is RestaurantAreaType => area in RestaurantAreas
  const isReady = computed(
    () => !!authStore.token && !!restaurantStore.restaurant && !!restaurantStore.areaSelected
  )
  const promoProduct = ref<PromotionDeepLinkDataType | null>(null)

  const requireLogin = () => {
    const route = ssoRoute({ register: false })
    return navigateTo(route, { external: true })
  }

  const handleProductRedirect = (
    productId: string,
    area: RestaurantAreaType,
    promo?: PromotionDeepLinkDataType
  ) => {
    restaurantStore.productIdentifierNotFound = false
    restaurantStore.productIdentifierAfterRestaurantSelected = productId
    restaurantStore.openChangeRestaurantModal()
    restaurantStore.candidateArea = area // select area to show button selected (no google maps loaded yet)
    willOpenArea.value = area
    // exist promo from deeplink
    if (promo) {
      // Case 1: user is NOT logged in → redirect to login/enroll
      if (!isUserLogged.value) {
        return requireLogin()
      }
      // Case 2: user is logged
      // Add promo in view product detail since deeplink
      promoProduct.value = promo
    }
  }

  const handleRestaurantRedirect = async (restaurantCode: string) => {
    restaurantStore.productIdentifierNotFound = false
    const { restaurant: restaurantFromCookie } = getSelectedRestaurantFromCookie()

    const isDifferingFromCookie = restaurantFromCookie?.code !== restaurantCode

    try {
      let restaurant: Restaurant | undefined
      if (isDifferingFromCookie)
        restaurant = await restaurantService.getStoreRestaurantByCode({
          countryCode: country,
          area: RestaurantAreas.MOP,
          restaurantCode,
        })

      const restaurantToUse = restaurant ?? restaurantFromCookie
      if (!restaurantToUse) {
        // show message error
        restaurantStore.productIdentifierNotFound = true
        debug('Error: No restaurant to select')
        return
      }
      if (restaurant) restaurantStore.setRestaurant(restaurant)
      const { code, name, city } = restaurantToUse
      await navigateToRestaurantHome({ code, name, city })
    } catch (error) {
      // show message error
      restaurantStore.productIdentifierNotFound = true
      debug('Error: No restaurant valid found', error)
    }
  }

  const applyPromotionFromDeeplinkToCart = async (promotion: PromotionDeepLinkDataType) => {
    // Navigate login enroll
    if (!isUserLogged.value) {
      return requireLogin()
    }
    const allowedPromotions: promotionTypeType[] = [
      PromotionType.ORDER_AMOUNT,
      PromotionType.ORDER_PERCENTAGE,
    ]
    const { restaurant: restaurantSelected } = getSelectedRestaurantFromCookie()
    // Go to restaurant home
    if (!restaurantSelected) {
      // show message error
      restaurantStore.productIdentifierNotFound = true
      debug('Error - Promotion : No restaurant valid found')
      return
    }
    const { code, name, city } = restaurantSelected
    await navigateToRestaurantHome({ code, name, city })
    // validate promotion type
    if (!promotion || !allowedPromotions.includes(promotion.type)) {
      // show message error
      restaurantStore.productIdentifierNotFound = true
      debug('Error - Promotion : Invalid promotion')
      return
    }
    // Add promotion cart
    addPromotionForCart(promotion, PromotionDeeplinkType.ORDER)
    return true
  }

  /**
   * Validates and applies a deeplink promotion to the cart.
   *
   * Flow:
   * 1. Waits until all stores are hydrated.
   * 2. Builds the verification request payload.
   * 3. Calls `/check-promos` to validate general promotion rules.
   *    - If invalid, shows a notification and stops the process.
   * 4. Calls `/check-code` for final coupon validation.
   * 5. If successful, stores the deeplink type in `appStore`
   *    and applies the promotion to the cart.
   *
   * This function safely handles API errors and unsupported types
   * without breaking existing flows.
   *
   * @param promo Promotion deeplink payload.
   * @param promoLinkType Normalized deeplink type ("ORDER" or "PRODUCT").
   */
  const addPromotionForCart = async (
    promo: PromotionDeepLinkDataType,
    promoLinkType: PromotionDeeplinkTypes
  ) => {
    // Wait until stores are hydrated
    await until(() => isReady.value).toBe(true)
    // Build verification input
    const checkPromosInput = getCheckPromosInput({
      promotionsId: [promo.promoId],
      products: [],
    })
    if (!checkPromosInput) return
    // Check if promotion is valid
    const checkPromosResponse = await promosService.checkPromos(checkPromosInput)
    const checkPromoResponse = checkPromosResponse[0] as Promotion
    if (showNotifyPromoError(checkPromoResponse)) return
    // Validate promotion code
    const body = getPromotionsBody()
    if (!body) return
    const promoResponse = await promosService.checkCode({
      ...body,
      coupon: checkPromosResponse[0].code!,
    })
    const couponPromotion = promoResponse as Promotion
    if (showNotifyPromoError(couponPromotion)) return
    // Show notification on product detail or cart view
    appStore.setDeeplinkPromotionType(promoLinkType)
    // apply promo to cart
    await applyPromotion({
      promo: couponPromotion,
      kind: PromotionKind.COUPON,
    })
  }

  const showNotifyPromoError = (promo: Promotion) => {
    const dialog = verifyCheckCodeResponse(promo)
    if (!dialog) return false

    const message = dialog.body || dialog.title
    showNotificationPromotion(ToastType.ERROR, message)

    return true
  }

  const showNotificationPromotion = (type: string, content: string) => {
    const { $toast } = useNuxtApp()
    $toast.show({
      type,
      content,
    })
  }

  const validateParamsEcommerce = async () => {
    const countryCode = route.query.c
    const productId = route.query.id as string
    const area = route.query.a as string
    const areaIsValid = isValidArea(area)
    const restaurantCode = route.query.r as string | undefined
    const promoId = route.query.promoid as string | undefined
    const promoType = route.query.promotype as promotionTypeType | undefined
    const promotionData: PromotionDeepLinkDataType | undefined =
      promoId && promoType ? { promoId, type: promoType } : undefined

    if (countryCode !== country) {
      // show message error
      restaurantStore.productIdentifierNotFound = true
      debug('Error: Country code mismatch')
      return
    }

    if (productId && area && areaIsValid) {
      return handleProductRedirect(productId, area, promotionData)
    }

    if (!productId && restaurantCode) {
      willOpenArea.value = RestaurantAreas.MOP
      restaurantStore.candidateArea = RestaurantAreas.MOP
      return handleRestaurantRedirect(restaurantCode)
    }
    // Add promotion with restaurant selected
    if (!productId && promotionData) {
      return applyPromotionFromDeeplinkToCart(promotionData)
    }

    // show message error
    restaurantStore.productIdentifierNotFound = true
    debug('Error: Missing required parameters in the URL')
  }

  watchEffect(async () => {
    if (initialFetchLoaded.value && willOpenArea.value) {
      // set area, and load googleMaps when initialFetch is loaded (apiKeys will be available)
      await restaurantStore.setArea({ area: willOpenArea.value, updateGoogleMaps: true })
    }
  })

  /**
   * Watches the state of the change-restaurant modal.
   *
   * When the modal closes (oldVal = true → newVal = false) and a stored
   * promotion exists in `promoProduct`, the promotion type is determined
   * ("ORDER" or "PRODUCT") and then applied to the cart.
   *
   * The watcher:
   * - Runs only when the modal fully closes.
   * - Prevents automatic application if the promotion type is not supported.
   * - Clears `promoProduct` after use.
   */
  watch(
    () => restaurantStore.modalChangeRestaurant,
    (newVal, oldVal) => {
      if (oldVal && !newVal && promoProduct.value) {
        const deeplinkType = getPromotionTypeToDeeplink(promoProduct.value.type)
        if (!deeplinkType) return
        addPromotionForCart(promoProduct.value, deeplinkType)
        promoProduct.value = null
      }
    }
  )

  return {
    validateParamsEcommerce,
    isError,
  }
}
